// Imports
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { PutPrivacyParameters } from '../../../../../../../shareables/types/api/main/website/member/settings/privacy/put';
import { GetPrivacyResponse } from '../../../../../../../shareables/types/api/main/website/member/settings/privacy/get';
import commonActions from '../../../../../../../shareables/foundation/front-end/redux/actions';
import Checkbox from '../../../../../../../shareables/foundation/front-end/components/forms/fields/checkbox';
import Radio from '../../../../../../../shareables/foundation/front-end/components/forms/fields/radio';
import Input from '../../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Phone from '../../../../../../../shareables/foundation/front-end/components/forms/fields/phone';
import MultiCheckboxContainer from '../../../../../../../shareables/foundation/front-end/components/forms/multi-checkbox-container';
import styled from 'styled-components/macro';
import { visuallyHiddenLabelStyle } from '../../../../../shared-components/visually-hidden-label';
import { GeneratedThemeProps } from '../../../../../../templates/template-style';
import { useOutletContext } from 'react-router-dom';


// Styled components
const Container = styled.div<{ theme: GeneratedThemeProps }>`
	line-height: 1.3125rem;
`;

const LeadershipNote = styled.small`
	font-weight: normal;
`;

const RadioContainer = styled.div`
	fieldset {
		margin-bottom: 0 !important;
	}
`;

const InputContainer = styled.div`
	margin-left: 1.25rem;
	margin-bottom: 1.25rem;
	max-width: 18.75rem;
	width: 70%;
	
	label {
		${visuallyHiddenLabelStyle}
	}
`;


// Define the accepted props
interface Props {
	storybookPrivacySettings?: GetPrivacyResponse;
}


// Function component
const MemberPrivacy: React.FC<Props> = ({ storybookPrivacySettings }) => {
	// Use state
	const [privacySettings, setPrivacySettings] = useState<GetPrivacyResponse | null>(storybookPrivacySettings ?? null);
	const [leaderEmailSelection, setLeaderEmailSelection] = useState(storybookPrivacySettings?.leaderEmail ?? '');
	const [leaderPhoneSelection, setLeaderPhoneSelection] = useState(storybookPrivacySettings?.leaderPhone ?? '');
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use ref
	const ref = useRef<HTMLDivElement | null>(null);
	
	
	// Use React Router functionality
	const updateHeight: (newHeight: number) => void = useOutletContext();
	
	
	// Update height for the members settings page
	useEffect(() => {
		if (!storybookPrivacySettings && ref.current) {
			updateHeight(ref.current.clientHeight);
		}
	}, [updateHeight, privacySettings, storybookPrivacySettings]);
	
	
	// Load contact settings on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetPrivacyResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/member/settings/privacy`,
				completion: (json) => {
					setPrivacySettings(json.response);
					setLeaderEmailSelection(json.response.leaderEmail);
					setLeaderPhoneSelection(json.response.leaderPhone);
				},
			})
		);
	}, [dispatch]);
	
	
	// If loading, then display nothing
	if (privacySettings === null) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PutPrivacyParameters, undefined, undefined> = {
		action: 'PUT',
		uri: `/website/${window.location.hostname}/member/settings/privacy`,
		submitButton: {
			text: 'Save',
		},
		recoverState: false,
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		dispatch(
			commonActions.showPageMessage({
				color: 'success',
				title: 'Success',
				message: 'These settings have been saved.',
			})
		);
	};
	
	
	// Return JSX
	return (
		<Container ref={ref}>
			<APIForm options={formOptions} onCompletion={handleFormCompletion}>
				<h4>Hide this information from the resident directory…</h4>
				<MultiCheckboxContainer>
					<Checkbox name='hideEmail' label='My email address' initiallyChecked={privacySettings.hideEmail} />
					<Checkbox name='hidePhone' label='My cell phone number' initiallyChecked={privacySettings.hidePhone} />
				</MultiCheckboxContainer>
				
				<h4>
					If I am listed in a leadership role…{' '}
					<LeadershipNote className='is-gray'>
						<em>e.g., board/committee member</em>
					</LeadershipNote>
				</h4>
				
				<RadioContainer>
					<Radio
						name='leaderEmail'
						initialValue={privacySettings.leaderEmail}
						onChange={setLeaderEmailSelection}
						choices={[
							{
								label: 'Show my email address',
								value: 'SHOW',
							},
							{
								label: 'Hide my email address',
								value: 'HIDE',
							},
							{
								label: 'Use this email address instead:',
								value: 'ALTERNATE',
							},
						]}
					/>
				</RadioContainer>
				<InputContainer>
					<Input
						type='email'
						name='alternateLeaderEmail'
						label='Alternate email address'
						placeholder='you@example.com'
						initialValue={privacySettings.alternateLeaderEmail ?? undefined}
						disabled={leaderEmailSelection !== 'ALTERNATE'}
					/>
				</InputContainer>
				
				<RadioContainer>
					<Radio
						name='leaderPhone'
						initialValue={privacySettings.leaderPhone}
						onChange={setLeaderPhoneSelection}
						choices={[
							{
								label: 'Show my cell phone number',
								value: 'SHOW',
							},
							{
								label: 'Hide my cell phone number',
								value: 'HIDE',
							},
							{
								label: 'Use this phone number instead:',
								value: 'ALTERNATE',
							},
						]}
					/>
				</RadioContainer>
				<InputContainer>
					<Phone
						name='alternateLeaderPhone'
						label='Alternate phone number'
						initialValue={privacySettings.alternateLeaderPhone ?? undefined}
						disabled={leaderPhoneSelection !== 'ALTERNATE'}
					/>
				</InputContainer>
			</APIForm>
		</Container>
	);
};

export default MemberPrivacy;
